import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { movies, categories } from '@/mocks/movies';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  const relatedMovies = movie
    ? movies
        .filter(
          (m) =>
            m.id !== movie.id &&
            (m.category === movie.category || m.genres.some((g) => movie.genres.includes(g)))
        )
        .slice(0, 6)
    : [];

  const getCategoryName = (slug: string) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [id]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center text-foreground-300 mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-800">影视未找到</h2>
            <p className="text-sm text-foreground-500 mt-2">该影视资源可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-block mt-4 px-5 py-2.5 text-sm bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="w-full px-4 md:px-6 py-3 bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-xs md:text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${movie.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {getCategoryName(movie.category)}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium truncate">{movie.title}</span>
          </div>
        </nav>

        <article className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full sm:w-[280px] mx-auto lg:mx-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    title={`${movie.title} ${movie.year} ${movie.region} - 天堂影院高清在线播放`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{movie.title}</h1>
                {movie.originalTitle && (
                  <p className="text-sm text-foreground-500 mt-1">{movie.originalTitle}</p>
                )}

                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <span className="flex items-center gap-1 px-3 py-1.5 bg-accent-100 text-accent-800 rounded-lg text-sm font-semibold">
                    <i className="ri-star-fill text-accent-500"></i> {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-4">
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {genre}
                    </span>
                  ))}
                  <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    {getCategoryName(movie.category)}
                  </span>
                </div>

                {movie.director && (
                  <div className="mt-4 flex items-start gap-2">
                    <span className="text-sm text-foreground-400 whitespace-nowrap">导演：</span>
                    <span className="text-sm text-foreground-800">{movie.director}</span>
                  </div>
                )}

                <div className="mt-2 flex items-start gap-2">
                  <span className="text-sm text-foreground-400 whitespace-nowrap">主演：</span>
                  <span className="text-sm text-foreground-800">{movie.actors.join(' / ')}</span>
                </div>

                <div className="mt-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-play-fill"></i> 立即播放
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-heart-line"></i> 收藏
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    <i className="ri-share-line"></i> 分享
                  </button>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-10 md:mt-14" aria-label="相关推荐">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <Link
                      key={m.id}
                      to={`/movie/${m.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden transition-all hover:border-primary-200 cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={m.poster}
                          alt={m.title}
                          title={`${m.title} - 相关推荐 天堂影院`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-xs px-1.5 py-0.5 rounded-md">
                          <i className="ri-star-fill text-accent-400 text-xs"></i>
                          <span>{m.rating}</span>
                        </div>
                      </div>
                      <div className="p-2.5 md:p-3">
                        <h3 className="font-heading text-xs md:text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {m.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{m.year}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}